export interface TimedPoint {
  elapsed_seconds: number;
  latitude: number;
  longitude: number;
  altitude_m: number | null;
  home_altitude_relative_m?: number | null;
  speed_mps?: number | null;
  horizontal_speed_mps?: number | null;
  vertical_rate_mps?: number | null;
  yaw_deg?: number | null;
  pitch_deg?: number | null;
  roll_deg?: number | null;
  fuel?: number | null;
  fuel_capacity?: number | null;
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function wrapHeadingDelta(delta: number): number {
  let v = delta;
  while (v > 180) v -= 360;
  while (v < -180) v += 360;
  return v;
}

export function interpolatePoint<T extends TimedPoint>(
  points: T[],
  elapsed: number,
): T | null {
  if (!points.length) return null;
  if (elapsed <= points[0].elapsed_seconds) return points[0];
  if (elapsed >= points[points.length - 1].elapsed_seconds) {
    return points[points.length - 1];
  }
  let lo = 0;
  let hi = points.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (points[mid].elapsed_seconds <= elapsed) lo = mid;
    else hi = mid;
  }
  const a = points[lo];
  const b = points[hi];
  const span = b.elapsed_seconds - a.elapsed_seconds;
  const t = span > 0 ? (elapsed - a.elapsed_seconds) / span : 0;

  const out = { ...a } as Record<string, unknown>;
  const aRec = a as unknown as Record<string, unknown>;
  const bRec = b as unknown as Record<string, unknown>;
  for (const key of Object.keys(aRec)) {
    const av = aRec[key];
    const bv = bRec[key];
    if (typeof av === "number" && typeof bv === "number") {
      if (key === "yaw_deg") {
        out[key] = (av + wrapHeadingDelta(bv - av) * t + 360) % 360;
      } else {
        out[key] = av + (bv - av) * t;
      }
    }
  }
  return out as unknown as T;
}

export function formatSpeedKmh(mps: number | null | undefined): string {
  if (mps === null || mps === undefined) return "—";
  return `${(mps * 3.6).toFixed(1)} km/h`;
}

export function formatAltitude(m: number | null | undefined): string {
  if (m === null || m === undefined) return "—";
  return `${m.toFixed(0)} m`;
}

export function normalizeHeading(value: number): number {
  const v = ((value % 360) + 360) % 360;
  return Math.round(v);
}

export function formatHeadingLabel(h: number): string {
  const n = normalizeHeading(h);
  if (n === 0) return "N";
  if (n === 90) return "E";
  if (n === 180) return "S";
  if (n === 270) return "W";
  return String(n).padStart(3, "0");
}

export interface HeadingTick {
  left: number;
  major: boolean;
  label: string;
}

export function computeHeadingTicks(heading: number, width: number): HeadingTick[] {
  const center = width / 2;
  const majorStepPx = 58;
  const minorStepDeg = 5;
  const centerTick = Math.round(heading / minorStepDeg) * minorStepDeg;
  const ticks: HeadingTick[] = [];
  for (let offset = -18; offset <= 18; offset += 1) {
    const rawValue = centerTick + offset * minorStepDeg;
    const normalized = normalizeHeading(rawValue);
    const delta = wrapHeadingDelta(normalized - heading);
    const left = center + (delta / 10) * majorStepPx;
    const isMajor = rawValue % 10 === 0;
    const showLabel = rawValue % 30 === 0;
    ticks.push({
      left,
      major: isMajor,
      label: showLabel ? formatHeadingLabel(normalized) : "",
    });
  }
  return ticks;
}

export interface TapeTick {
  top: number;
  major: boolean;
  label: string;
}

export function computeTapeTicks(
  value: number,
  step: number,
  range: number,
  majorEvery: number,
  tapeHeight: number,
): TapeTick[] {
  const centerY = tapeHeight / 2;
  const spacing = clamp((tapeHeight - 42) / (range * 2 + 2), 16, 26);
  const base = Math.round(value / step) * step;
  const ticks: TapeTick[] = [];
  for (let offset = -range; offset <= range; offset += 1) {
    const tickValue = base + offset * step;
    const top = centerY - ((tickValue - value) / step) * spacing;
    const isMajor = majorEvery > 0 && Math.abs(tickValue % majorEvery) < 0.0001;
    ticks.push({
      top,
      major: isMajor,
      label: isMajor ? String(Math.round(tickValue)) : "",
    });
  }
  return ticks;
}
